/**
 * StateReports — /states/reports
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Download, ExternalLink } from "lucide-react";
import { PageHeader, SectionCard, CTAButton } from "@/components/ds";

const REPORT_CATALOG = [
  {
    id: "state-climate-credentials",
    label: "State Climate & Circularity Credentials",
    desc: "Aggregated credentials document with 7-section institutional structure.",
    type: "PDF", path: "/states/credentials",
  },
  {
    id: "aggregated-projects",
    label: "State Project Portfolio Report",
    desc: "Full list of aggregated projects with reduction potential and CAPEX.",
    type: "View", path: "/states/aggregated-projects",
  },
  {
    id: "article6-readiness",
    label: "Article 6 Readiness Package",
    desc: "Assessment of Article 6 readiness gates for this geography.",
    type: "View", path: "/states/carbon-pathways",
  },
  {
    id: "programme-docs",
    label: "Programme Structuring Documents",
    desc: "PoA / CPA / NDC programme registry for this geography.",
    type: "View", path: "/states/programmes",
  },
  {
    id: "cai-portfolio",
    label: "CAI ITMO Portfolio Export",
    desc: "ITMO transaction records and corresponding adjustments from CAI workspace.",
    type: "External", path: "/cai/reporting",
  },
];

export default function StateReports() {
  const navigate = useNavigate();

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader
        label="STATES WORKSPACE"
        title="Reports & Regulatory Exports"
        subtitle="Institutional outputs for regulators, DFIs, and government bodies"
        backUrl="/states/overview"
        backLabel="State Overview"
      />

      <MetricCard label="Available Reports" value={REPORT_CATALOG.length} color="text-slate-900" className="inline-block" />

      <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-3">
        <p className="text-xs text-amber-700">
          All reports are generated from platform-aggregated data. They are not official government
          climate inventories. For regulatory use, independent verification is required.
        </p>
      </div>

      <SectionCard title="Report Catalog" noPadding>
        {REPORT_CATALOG.map(r => (
          <div key={r.id} className="flex items-center gap-4 px-5 py-4 border-b border-slate-50 last:border-0">
            <FileText size={16} className="text-teal-600 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-900">{r.label}</p>
              <p className="text-xs text-slate-400 mt-0.5">{r.desc}</p>
            </div>
            <button
              onClick={() => navigate(r.path)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-xl flex-shrink-0 transition-colors ${
                r.type === "PDF"
                  ? "bg-teal-600 text-white hover:bg-teal-700"
                  : r.type === "External"
                  ? "border border-blue-200 text-blue-700 hover:bg-blue-50"
                  : "border border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
            >
              {r.type === "External" ? <ExternalLink size={11} /> : <Download size={11} />}
              {r.type}
            </button>
          </div>
        ))}
      </SectionCard>
    </div>
  );
}
